<template>
  <div class="pa-4">
    <v-card-text class="font-weight-medium text-uppercase"
      >CUSTOM IMAGE</v-card-text
    >
    <v-img :src="imageUrl" width="150px" class="px-4">
      <template v-slot:placeholder>
        <v-img class="mx-auto" width="150px" :src="noVehicleImage"></v-img>
      </template>
    </v-img>
    <div class="d-flex flex-column">
      <v-select
        density="compact"
        class="px-4"
        label="Year"
        v-model="selectedYear"
        :items="availableYears.sort((a, b) => b - a)"
      ></v-select>
      <v-select
        density="compact"
        class="px-4"
        label="Make"
        v-model="selectedMake"
        :disabled="!selectedYear && availableMakes.length === 0"
        :items="
          availableMakes.sort((a: string, b: string) => a.localeCompare(b))
        "
      ></v-select>
      <v-select
        density="compact"
        class="px-4"
        label="Model"
        v-model="selectedModel"
        :disabled="!selectedMake"
        :items="
          availableModels.sort((a: string, b: string) => a.localeCompare(b))
        "
      ></v-select>
      <v-select
        density="compact"
        class="px-4"
        label="Trim"
        v-model="selectedTrim"
        :disabled="!selectedModel"
        :items="
          availableTrims.sort((a: string, b: string) => a.localeCompare(b))
        "
      ></v-select>
      <v-select
        density="compact"
        class="px-4"
        label="Color"
        v-model="selectedColor"
        :disabled="!selectedTrim"
        :items="availableColors"
      >
        <template v-slot:item="{ props, item }">
          <div
            class="d-flex pa-4 ga-2 align-center cursor-pointer"
            v-bind="props"
          >
            <p>{{ item.title }}</p>
            <div
              :style="{ backgroundColor: '#' + item.title }"
              style="width: 20px; height: 20px"
            ></div>
          </div>
        </template>
      </v-select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/settings'
import { useVehiclesStore } from '@/stores/vehicles'
import { onMounted, ref, watch } from 'vue'
import noVehicleImage from '@/assets/no-vehicle-image.png'
import {
  getAvailableMakesForFilters,
  getAvailableModelsForFilters,
  getAvailableTrimsForModel,
} from '@/api'

const props = defineProps<{
  selectedPlacement: string
  make: string
  model: string
}>()

const apiUrl = import.meta.env.VITE_VEHICLE_IMG_URL

const settingsStore = useSettingsStore()
const vehicleStore = useVehiclesStore()

const modelValue = ref()

const selectedYear = ref()
const selectedMake = ref()
const selectedModel = ref()
const selectedTrim = ref()
const selectedColor = ref()

const availableYears = ref([])
const availableMakes = ref([])
const availableModels = ref([])
const availableTrims = ref([])
const availableColors = ref([])

const imageUrl = ref('')

onMounted(async () => {
  availableYears.value = await vehicleStore.getAvailableYears()
  modelValue.value = settingsStore.placements.placements[
    props.selectedPlacement
  ]['filter'].find((item: any) => {
    return item.make === props.make && item.model === props.model
  })
  if (modelValue.value.settings.customImg.length > 0) {
    imageUrl.value = modelValue.value.settings.customImg
  }
})

const getTrimStyleId = async (trim: string) => {
  const response = await getAvailableTrimsForModel(
    selectedYear.value,
    selectedMake.value,
    selectedModel.value,
  )
  return response.trims.filter(
    (item: any) => item.styleName === selectedTrim.value,
  )[0].styleId
}

const getImageSrc = async () => {
  if (selectedTrim.value) {
    const styleId = await getTrimStyleId(selectedTrim.value) // Await the Promise
    return (imageUrl.value = `${apiUrl}?styleId=${styleId}`)
  } else if (selectedColor.value) {
    return (imageUrl.value = `${apiUrl}?styleId=${selectedColor.value}`)
  } else {
    return (imageUrl.value = `${apiUrl}?make=${selectedMake.value}&model=${selectedModel.value}`)
  }
}

watch(selectedYear, async () => {
  const response = await getAvailableMakesForFilters(selectedYear.value)
  availableMakes.value = response.makes

  // reset states
  selectedMake.value = undefined
  selectedModel.value = undefined
  selectedTrim.value = undefined
  selectedColor.value = undefined
})

watch(selectedMake, async () => {
  const response = await getAvailableModelsForFilters(
    selectedYear.value,
    selectedMake.value,
  )
  availableModels.value = response.models

  // reset states
  selectedModel.value = undefined
  selectedTrim.value = undefined
  selectedColor.value = undefined
})

watch(selectedModel, async () => {
  const response = await getAvailableTrimsForModel(
    selectedYear.value,
    selectedMake.value,
    selectedModel.value,
  )
  availableTrims.value = response.trims.map((item: any) => item.styleName)
  availableColors.value = response.trims
    .filter((item: any) => item.styleName === selectedTrim.value)
    .map((item: any) => item.styleId)

  getImageSrc()

  // reset states
  selectedColor.value = undefined
  selectedTrim.value = undefined
})

watch(selectedTrim, async () => {
  const response = await getAvailableTrimsForModel(
    selectedYear.value,
    selectedMake.value,
    selectedModel.value,
  )

  availableColors.value = response.trims
    .filter((item: any) => item.styleName === selectedTrim.value)
    .map((item: any) => item.styleId)

  getImageSrc()
})

watch(selectedColor, () => {
  getImageSrc()
})

watch(imageUrl, () => {
  const item = settingsStore.placements.placements[props.selectedPlacement][
    'filter'
  ].find((item: any) => {
    return item.make === props.make && item.model === props.model
  })

  item.settings.customImg = imageUrl.value
})
</script>
